import { Alert, Box, Tooltip, Typography } from "@mui/material";
import type { KeyboardEvent } from "react";
import { KPI_CARD_HEIGHT_PX } from "@/constants";
import type { AppKpiFormat } from "@/features/app-studio/api/appKpisApi";

type AppKpiCardViewProps = {
  name: string;
  format: AppKpiFormat;
  value?: unknown;
  error?: string | null;
  /**
   * Render the label and value at a larger type scale — used in the
   * published app view, where the KPI card is the primary content shown to
   * end users rather than a compact builder-panel tile.
   */
  largeContent?: boolean;
  /**
   * Fixed card height override (px). Defaults to KPI_CARD_HEIGHT_PX (the
   * app-studio builder sizing). The published app view passes
   * PUBLISHED_KPI_CARD_HEIGHT_PX instead, since its chart/table cards carry
   * an extra summary block that the builder's don't.
   */
  heightPx?: number;
  /**
   * Open this card's drill-down widget. When set the whole card becomes a button — pointer cursor
   * and an accent border on hover. Only passed when the card actually has a resolvable drill-down
   * widget. No corner icon: `OpenInFull` is a chart/table's real expand action elsewhere in the
   * app, so on a card that opens a *different* widget it read as "make this bigger".
   */
  onDrilldown?: () => void;
  /** Accessible name for the drill-down action; required whenever `onDrilldown` is set. */
  drilldownAriaLabel?: string;
};

function formatKpiValue(value: unknown, format: AppKpiFormat): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (format === "text") {
    return String(value);
  }

  const numeric = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }

  switch (format) {
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(numeric);
    case "percent":
      return `${new Intl.NumberFormat().format(numeric)}%`;
    case "number":
    default:
      return new Intl.NumberFormat().format(numeric);
  }
}

export function AppKpiCardView({
  name,
  format,
  value,
  error,
  largeContent = false,
  heightPx = KPI_CARD_HEIGHT_PX,
  onDrilldown,
  drilldownAriaLabel,
}: AppKpiCardViewProps) {
  // An errored KPI renders as an alert rather than a card, so it is never a drill-down source
  // even when one is configured — there is no card body to click.
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }

  const formattedValue = formatKpiValue(value, format);
  const isDrillable = onDrilldown !== undefined;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    // Space would otherwise scroll the page while the card has focus.
    event.preventDefault();
    onDrilldown?.();
  };

  return (
    <Box
      role={isDrillable ? "button" : undefined}
      tabIndex={isDrillable ? 0 : undefined}
      aria-label={isDrillable ? drilldownAriaLabel : undefined}
      onClick={isDrillable ? onDrilldown : undefined}
      onKeyDown={isDrillable ? handleKeyDown : undefined}
      sx={{
        height: `${heightPx}px`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        px: 2,
        pt: 2,
        minWidth: 0,
        borderRadius: 2,
        border: "1px solid var(--theme-border)",
        bgcolor: "var(--theme-surface)",
        ...(isDrillable
          ? {
              cursor: "pointer",
              transition: "border-color 120ms ease-in-out",
              "&:hover": { borderColor: "var(--theme-accent)" },
              "&:focus-visible": {
                outline: "2px solid var(--theme-accent)",
                outlineOffset: 2,
              },
            }
          : {}),
      }}
    >
      <Tooltip title={name} followCursor arrow>
        <Typography
          variant={largeContent ? "h6" : "body2"}
          noWrap
          sx={{
            color: "var(--theme-text-secondary)",
            fontWeight: largeContent ? 600 : 400,
          }}
        >
          {name}
        </Typography>
      </Tooltip>
      <Tooltip title={formattedValue} followCursor arrow>
        <Typography
          variant={largeContent ? "h3" : "h4"}
          noWrap
          sx={{ fontWeight: 700, color: "var(--theme-text-primary)" }}
        >
          {formattedValue}
        </Typography>
      </Tooltip>
    </Box>
  );
}
